import React, {useState} from 'react';
import {Modal, Pressable, ScrollView, StatusBar, StyleSheet, Text, View} from 'react-native';
import {useSafeAreaInsets} from 'react-native-safe-area-context';

import {control, radius, space, type, universal} from '../../design-system/tokens';

import BackArrowImg from './assets/BackArrow.svg';
import ChevronRightImg from './assets/ChevronRight.svg';
import CloseImg from './assets/Close.svg';
import MenuIconBalancedLifestyleImg from './assets/menu-icon-balanced-lifestyle.svg';
import MenuIconBeerImg from './assets/menu-icon-beer.svg';
import MenuIconPremixImg from './assets/menu-icon-premix.svg';
import MenuIconRedWineImg from './assets/menu-icon-red-wine.svg';
import MenuIconSpecialsImg from './assets/menu-icon-specials.svg';
import MenuIconSpiritsImg from './assets/menu-icon-spirits.svg';
import MenuGiftsImg from './assets/MenuGifts.svg';
import type {MenuAction, PromoTile, ResolvedGroup, ResolvedMenu} from './menu/menuTypes';
import {useBand} from './breakpoints';
import {chromeType} from './chromeType';
import {headerChrome} from './headerChrome';
import {BANNER_ASPECT, PanelPlaceholder} from './MegaMenuPanel';
import {MenuLinkText} from './MenuLinkText';

// SHORTCUT: layout and colour literals from header/dropdown/mob (130:9970, 441:37111,
// 470:20199); the redline token sheet records each as a token gap. Row height 64 and the
// 32 sub-row are heights, not spacing. Grey Light #efefef is a bound Figma variable with no
// `universal` member; the #f9faff tint is unbound. Ceiling: PR B. Upgrade path: DIS-1735 adds
// universal.greyLight, a brand surface-tint token and a menu row height token.
const ROW_HEIGHT = 64;
const SUB_ROW_HEIGHT = 32;
const STORE_CARD_HEIGHT = 72;
const GREY_LIGHT = '#efefef';
const TINT = '#f9faff';
// SHORTCUT: icon sizes are Figma literals with no icon-size token (menu-item/icons 77:4992:
// category 32, controls 24, back arrow 18). Upgrade path: a DIS-1735 icon-size scale. Each
// SVG call site repeats the file's own viewBox, which the web svgr config strips.
const CATEGORY_ICON_SIZE = 32;
const CONTROL_ICON_SIZE = 24;
const BACK_ICON_SIZE = 18;
// SHORTCUT: scrim black at 50 % has no Figma source and no token (no frame draws the sheet
// over a page). Upgrade path: design specifies the scrim, or DIS-1735 adds an overlay token.
const SCRIM_OPACITY = 0.5;
// SHORTCUT (R61): no Figma frame draws the sheet on desktop-class bands (>= 1024). It renders
// at its mobile frame width (441:37111, 375), anchored left over the scrim, with the mobile
// content. Ceiling: no desktop Figma frame. Upgrade path: a designer frame for the desktop sheet.
export const SHEET_FRAME_WIDTH = 375;
// Figma's promo gap is 7.69 (a scaled instance; verdict "artefact"); space.s8 is used.
const PROMO_GAP = space.s8;

type IconComponent = typeof MenuIconSpecialsImg;
type CategoryIcon = {Component: IconComponent; width: number; height: number; viewBox: string};
const icon32 = (Component: IconComponent): CategoryIcon => ({
  Component,
  width: CATEGORY_ICON_SIZE,
  height: CATEGORY_ICON_SIZE,
  viewBox: `0 0 ${CATEGORY_ICON_SIZE} ${CATEGORY_ICON_SIZE}`,
});
/**
 * Figma menu-item/icons 77:4992, keyed by menu id (menuConfig.ts). New takes the Specials star
 * and Gifts & Occasions the "new" box, as header/dropdown/mob 130:9970 draws them.
 */
const CATEGORY_ICON: Record<string, CategoryIcon> = {
  specials: icon32(MenuIconSpecialsImg),
  'beer-cider': icon32(MenuIconBeerImg),
  wine: icon32(MenuIconRedWineImg),
  spirits: icon32(MenuIconSpiritsImg),
  premix: icon32(MenuIconPremixImg),
  // SHORTCUT: 32.0586 × 29 is the Figma size of "balanced lifestyle" (77:4986), off the 32
  // icon grid. Upgrade path: design re-exports it on a 32 × 32 frame.
  'non-alcoholic': {
    Component: MenuIconBalancedLifestyleImg,
    width: 32.0586,
    height: 29,
    viewBox: '0 0 32.0586 29',
  },
  new: icon32(MenuIconSpecialsImg),
  gifts: icon32(MenuGiftsImg),
};

export type MobileMenuSheetProps = {
  /** The menus to list: SiteHeader passes none for a non-ecommerce store or a failed load. */
  menus: ResolvedMenu[];
  /** Departments still loading (`idle`): a submenu shows the placeholder (Q3). */
  loading: boolean;
  /** Store card name: the store, NO_STORE_COPY or GIFT_CARD_STORE_COPY. */
  storeName: string;
  /** Store card availability copy; undefined hides the line. */
  availability?: string;
  /** "Change store"; undefined hides the link (gift-card store, R42). */
  onStorePress?: () => void;
  /** A live menu link was pressed (BrandHeader's `handleMenuAction`). */
  onAction(a: MenuAction): void;
  onClose(): void;
  /** Opens a category submenu directly (stories). */
  menuId?: string;
};

const CloseButton = ({onClose}: {onClose: () => void}) => (
  <Pressable onPress={onClose} accessibilityRole="button" accessibilityLabel="Close menu">
    <CloseImg
      viewBox={`0 0 ${CONTROL_ICON_SIZE} ${CONTROL_ICON_SIZE}`}
      width={CONTROL_ICON_SIZE}
      height={CONTROL_ICON_SIZE}
      color={universal.black}
      aria-hidden={true}
    />
  </Pressable>
);

/**
 * A group: heading, then its items, collapsed at first. DIS-2043: the whole strip toggles, and
 * the chevron (Figma 94:1671) points up while open. A group with nothing to expand keeps its
 * heading link (Gift Cards, menuConfig.ts) and has no chevron.
 */
const Group = ({group, onAction}: {group: ResolvedGroup; onAction(a: MenuAction): void}) => {
  const [open, setOpen] = useState(false);
  if (!group.items.length && !group.shopAll) {
    return (
      <View style={styles.group}>
        <View style={styles.groupHeading}>
          <MenuLinkText link={group.heading} style={styles.heading} onAction={onAction} />
        </View>
      </View>
    );
  }
  return (
    <View style={styles.group}>
      <Pressable
        testID="site-header-menu-group-strip"
        onPress={() => setOpen(current => !current)}
        accessibilityRole="button"
        accessibilityLabel={group.heading.label}
        // aria-expanded, not accessibilityState: react-native-web 0.21 drops the latter.
        aria-expanded={open}
        style={[styles.groupHeading, styles.groupStrip]}>
        <Text style={styles.heading}>{group.heading.label}</Text>
        <View style={open ? styles.chevronUp : styles.chevronDown}>
          <ChevronRightImg
            viewBox="0 0 16 16"
            width={CONTROL_ICON_SIZE}
            height={CONTROL_ICON_SIZE}
            color={universal.black}
            aria-hidden={true}
          />
        </View>
      </Pressable>
      {open ? (
        <>
          {group.items.map((item, i) => (
            <MenuLinkText
              key={i}
              link={item}
              style={[styles.item, styles.subRow]}
              onAction={onAction}
            />
          ))}
          {group.shopAll ? (
            <MenuLinkText
              link={group.shopAll}
              style={[styles.accentLink, styles.subRow]}
              onAction={onAction}
            />
          ) : null}
        </>
      ) : null}
    </View>
  );
};

/**
 * Promo tiles, two squares per row and wide banners across. A tile with an `action` is a link,
 * as on desktop (DIS-1919, MegaMenuPanel `Promo`); one without stays a plain view.
 */
const Promos = ({promos, onAction}: {promos: PromoTile[]; onAction(a: MenuAction): void}) => {
  const [gridWidth, setGridWidth] = useState(0);
  const square = (gridWidth - PROMO_GAP) / 2;
  return (
    <View
      testID="site-header-menu-sheet-promos"
      style={styles.promos}
      onLayout={e => setGridWidth(e.nativeEvent.layout.width)}>
      {gridWidth > 0 &&
        promos.map((tile, i) => {
          const width = tile.wide ? gridWidth : square;
          const height = tile.wide ? gridWidth / BANNER_ASPECT : square;
          const art = <tile.image width={width} height={height} />;
          const action = tile.action;
          if (!action) {
            return (
              <View key={i} style={[styles.promo, {width, height}]} accessibilityLabel={tile.label}>
                {art}
              </View>
            );
          }
          return (
            <Pressable
              key={i}
              onPress={() => onAction(action)}
              accessibilityRole="link"
              accessibilityLabel={tile.label}
              style={[styles.promo, {width, height}]}>
              {art}
            </Pressable>
          );
        })}
    </View>
  );
};

/** header/dropdown/mob 441:37111: the submenu's title row, back to the categories and close. */
const SubmenuTitle = ({
  label,
  onBack,
  onClose,
}: {
  label: string;
  onBack: () => void;
  onClose: () => void;
}) => (
  <View style={[styles.titleRow, styles.tinted]}>
    <Pressable
      onPress={onBack}
      style={styles.backButton}
      accessibilityRole="button"
      accessibilityLabel="Back to all categories">
      <BackArrowImg
        viewBox={`0 0 ${BACK_ICON_SIZE} ${BACK_ICON_SIZE}`}
        width={BACK_ICON_SIZE}
        height={BACK_ICON_SIZE}
        color={universal.black}
        aria-hidden={true}
      />
      <Text style={styles.title}>{label}</Text>
    </Pressable>
    <CloseButton onClose={onClose} />
  </View>
);

/** header/dropdown/mob 441:37111, 470:20199: one category, its groups collapsed until tapped. */
const Submenu = ({
  menu,
  loading,
  onAction,
}: {
  menu: ResolvedMenu;
  loading: boolean;
  onAction(a: MenuAction): void;
}) => (
  <View style={styles.submenu}>
    {loading ? (
      <View style={[styles.column, styles.loading]}>
        <PanelPlaceholder />
      </View>
    ) : (
      <View style={styles.column}>
        {menu.mainLinks.map((link, i) => (
          <View key={i} style={[styles.row, styles.divided]}>
            <MenuLinkText link={link} style={styles.accentLink} onAction={onAction} />
          </View>
        ))}
        {menu.groups.map((group, i) => (
          <Group key={`${menu.id}-${i}`} group={group} onAction={onAction} />
        ))}
        {menu.promos.length > 0 && <Promos promos={menu.promos} onAction={onAction} />}
      </View>
    )}
  </View>
);

/**
 * The web menu sheet (DIS-1898 B9), ported from the header prototype (ea875fae6
 * MobileMenu.tsx). It replaces the drawer on web: the menus (header/dropdown/mob 130:9970),
 * then the selected store.
 *
 * A react-native Modal: on web (react-native-web 0.21) it is an `aria-modal` dialog that traps
 * focus, calls `onRequestClose` on Escape, and on close returns focus to the element focused
 * when it opened (the menu button). Fade is an opacity animation.
 */
export const MobileMenuSheet = ({
  menus,
  loading,
  storeName,
  availability,
  onStorePress,
  onAction,
  onClose,
  menuId,
}: MobileMenuSheetProps) => {
  const [openId, setOpenId] = useState(menuId);
  const desktopClass = useBand() !== 'mobile';
  const shown = menus.find(menu => menu.id === openId);
  // DIS-1902: the Modal draws edge-to-edge on Android, under both system bars. 0 on web.
  const insets = useSafeAreaInsets();

  const handleAction = (a: MenuAction) => {
    onClose();
    onAction(a);
  };

  return (
    // react-native-web 0.21 passes the Modal's other props to the `role="dialog"` element, so
    // the label names the dialog itself.
    <Modal visible transparent animationType="fade" onRequestClose={onClose} aria-label="Menu">
      {/* DIS-1902: the white sheet sits under the status bar; the app default is white icons. */}
      <StatusBar barStyle="dark-content" />
      <View style={styles.overlay}>
        <View
          testID="site-header-menu-sheet"
          accessibilityViewIsModal
          style={[
            styles.sheet,
            {
              width: desktopClass ? SHEET_FRAME_WIDTH : '100%',
              paddingTop: insets.top,
              paddingBottom: insets.bottom,
            },
          ]}>
          {/* The title row sits above the ScrollView, not inside it (gate B D1). The web Modal's
              focus trap (react-native-web 0.21 ModalFocusTrap) focuses the first node in DOM
              order that accepts `focus()`. Chrome 155 accepts `focus()` on a scroll container,
              even with tabindex -1 (measured), so the scroll container won over a close button
              inside it. Here "Close menu" comes first; the row stays put while the list scrolls. */}
          {shown ? (
            <SubmenuTitle
              label={shown.label}
              onBack={() => setOpenId(undefined)}
              onClose={onClose}
            />
          ) : (
            <View style={styles.column}>
              <View style={[styles.row, styles.divided]}>
                <Text style={styles.title}>Shop All</Text>
                <CloseButton onClose={onClose} />
              </View>
            </View>
          )}
          <ScrollView contentContainerStyle={styles.scrollContent}>
            {shown ? (
              <Submenu menu={shown} loading={loading} onAction={handleAction} />
            ) : (
              <>
                <View style={styles.column}>
                  {menus.map(menu => {
                    const icon = CATEGORY_ICON[menu.id] ?? CATEGORY_ICON.specials;
                    return (
                      <Pressable
                        key={menu.id}
                        testID={`site-header-menu-sheet-category-${menu.id}`}
                        onPress={() => setOpenId(menu.id)}
                        style={({pressed}) => [
                          styles.row,
                          styles.divided,
                          pressed && styles.rowActive,
                        ]}
                        accessibilityRole="button"
                        accessibilityLabel={menu.label}>
                        <View style={styles.category}>
                          <icon.Component
                            viewBox={icon.viewBox}
                            width={icon.width}
                            height={icon.height}
                            color={headerChrome.accent}
                            aria-hidden={true}
                          />
                          <Text style={styles.categoryLabel}>{menu.label}</Text>
                        </View>
                        <ChevronRightImg
                          viewBox="0 0 16 16"
                          width={CONTROL_ICON_SIZE}
                          height={CONTROL_ICON_SIZE}
                          color={headerChrome.accent}
                          aria-hidden={true}
                        />
                      </Pressable>
                    );
                  })}
                </View>
                <View
                  testID="site-header-menu-sheet-store-card"
                  style={[styles.storeCard, styles.tinted]}>
                  <View style={styles.storeText}>
                    <Text style={styles.storeName}>{storeName}</Text>
                    {availability ? (
                      <Text style={styles.storeAvailability}>{availability}</Text>
                    ) : null}
                  </View>
                  {onStorePress ? (
                    <Pressable
                      onPress={() => {
                        onClose();
                        onStorePress();
                      }}
                      accessibilityRole="link"
                      accessibilityLabel="Change store">
                      <Text style={styles.changeStore}>Change store</Text>
                    </Pressable>
                  ) : null}
                </View>
              </>
            )}
          </ScrollView>
        </View>
        {/* After the sheet, so the focus trap's first focus lands in the sheet (its close
            button), not on the scrim; the sheet's zIndex keeps it painted above. */}
        <Pressable
          testID="site-header-menu-sheet-scrim"
          onPress={onClose}
          accessible={false}
          focusable={false}
          style={[StyleSheet.absoluteFill, styles.scrim]}
        />
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {flex: 1},
  scrim: {
    backgroundColor: universal.black,
    opacity: SCRIM_OPACITY,
  },
  // Full height, anchored left: the full width on mobile, the 375 frame on desktop (R61).
  sheet: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    zIndex: 1,
    backgroundColor: universal.white,
  },
  // The store card sits at the bottom of the sheet: `flexGrow: 1` with `space-between` pushes it
  // below the category rows (Figma header/dropdown/mob 470:19681).
  scrollContent: {flexGrow: 1, justifyContent: 'space-between'},
  submenu: {paddingBottom: space.s40},
  loading: {paddingTop: space.s24},
  tinted: {backgroundColor: TINT},
  column: {paddingHorizontal: space.s16},
  row: {
    minHeight: ROW_HEIGHT,
    paddingVertical: space.s8,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  divided: {borderBottomWidth: control.borderWidth, borderBottomColor: GREY_LIGHT},
  rowActive: {backgroundColor: GREY_LIGHT},
  title: {...chromeType.title20SemiBold, color: universal.black},
  titleRow: {
    height: ROW_HEIGHT,
    paddingHorizontal: space.s16,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  backButton: {flexDirection: 'row', alignItems: 'center', gap: space.s12},
  category: {flexDirection: 'row', alignItems: 'center', gap: space.s16},
  categoryLabel: {...chromeType.label18SemiBold, color: universal.black},
  accentLink: {...chromeType.label18Medium, color: headerChrome.accent},
  group: {
    paddingVertical: space.s16,
    gap: space.s24,
    borderBottomWidth: control.borderWidth,
    borderBottomColor: GREY_LIGHT,
  },
  groupHeading: {
    height: SUB_ROW_HEIGHT,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  // The press target reaches over the group's vertical padding, with no change to the layout.
  groupStrip: {
    height: SUB_ROW_HEIGHT + 2 * space.s16,
    marginVertical: -space.s16,
    paddingVertical: space.s16,
  },
  heading: {...chromeType.label18Medium, color: universal.black},
  chevronUp: {transform: [{rotate: '-90deg'}]},
  chevronDown: {transform: [{rotate: '90deg'}]},
  // Figma 94:1677: 32-tall rows, text centred, 32 right inset. Listed last so it wins.
  subRow: {lineHeight: SUB_ROW_HEIGHT, paddingRight: space.s32},
  item: {...chromeType.body18Regular, color: universal.black},
  promos: {flexDirection: 'row', flexWrap: 'wrap', gap: PROMO_GAP},
  promo: {borderRadius: radius.card, overflow: 'hidden'},
  storeCard: {
    height: STORE_CARD_HEIGHT,
    paddingHorizontal: space.s16,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  storeText: {gap: space.s4, flexShrink: 1},
  storeName: {...chromeType.label15Medium, color: universal.black},
  storeAvailability: {...chromeType.body15Light, color: universal.black},
  changeStore: {
    ...type.button.tertiary,
    color: headerChrome.accent,
    textDecorationLine: 'underline',
  },
});
